import { Suspense, useEffect, useState } from "react";

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Input, useDisclosure, useToast } from "@chakra-ui/react"
import { Field, FieldProps, Form, Formik, FormikProps } from "formik"
import { useLocation, useNavigate } from "react-router-dom"
import { HttpStatusCode } from "axios";
import { format } from "date-fns";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import ArrowIcon from "@/assets/ArrowIcon";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import { COLORS, CURRENCY, DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST, FORMAT_ADD_DATE, INVOICE_STATUS, PAYMENT_STATUS } from "@/common/constants";
import CurrencyInput from "@/components/Fields/CurrencyInput";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";

import '../customDatepicker.css';
import '../SireInvoiceForm.css'
import Loader from "@/components/Layouts/Loader";
import TableSireReviewSimple, { SireDataSimple } from "@/components/Tables/TableSireReviewSimple";
import MonthSelector from "@/components/Dashboard/MonthSelector";
import EditFile from "@/components/Fields/EditFile";
import SelectCurrency from "@/components/Selects/SelectCurrency";
import { getListSireReviewsSimpleByInvoice, getListSireReviewsSimpleForReviewer, getSireReviewReviewerInvoiceDetails, postCreateInvoicesSireReviewReviewer, postUpdateSireReviewReviewerInvoice } from "@/services/InvoiceSireReviewReviewerService";
import { invoiceSireReviewReviewerSchema } from "@/schemas/invoiceSireReviewReviewerSchema";
import { SireReviewSimpleRequestor } from "@/services/InvoiceSireReviewRequestorService";

type FormContents = {
    invoiceID: string,
    invoiceDate: string,
    currency: string,
    reviewCost: number,
    invoiceFile: File[],
};

const AddInvoicesSireInspector = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const role = useAppSelector((state: RootState) => state.account.roleName);
    const companyId = useAppSelector((state: RootState) => state.account.appId);
    const companyName = useAppSelector((state: RootState) => state.account.companyName);
    const now = new Date();
    const currentMonth = now.getMonth() + 1; // getMonth() returns 0-11
    const currentYear = now.getFullYear();
    
    const location = useLocation();
    const { id } = location.state || {};

    const initial: FormContents = {
        invoiceID: '',
        invoiceDate: now.toDateString(),
			currency: 'IDR',
        reviewCost: 0,
        invoiceFile: [],
    };

    const [confirmationText, setConfirmationTest] = useState<string>("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState<string>("");
    const [dataAdd, setDataAdd] = useState<FormContents>(initial);
    const [isDraft, setIsDraft] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [prefix, setPrefix] = useState<string>('IDR');
    const [currency, setCurrency] = useState<string>(CURRENCY.IDR.code)
    const [title, setTitle] = useState<string>("");
    const [selectedMonth, setSelectedMonth] = useState<number>((currentMonth === 1) ? 12 : (currentMonth - 1));
    const [selectedYear, setSelectedYear] = useState<number>((currentMonth === 1) ? (currentYear - 1) : currentYear);
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [reviewCost, setReviewCost] = useState<number>(0);

    const [listSireReview, setListSireReview] = useState<SireDataSimple[]>([]);
    const [totalReviews, setTotalReviews] = useState<number>(0);
    const [totalCost, setTotalCost] = useState<number>(0);
    const [currentPageNumber, setCurrentPageNumber] = useState<number>(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
    const [hasNextPage, setHasNextPage] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [totalPageNumber, setTotalPageNumber] = useState<number>(1);

    const [initialContents, setInitialContents] = useState<FormContents>(initial)

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel Creating Invoice?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    const handleSubmit = (data: any) => {
        if (listSireReview.length === 0) {
            showToast({ code: 'No suitable SIRE reviews', message: 'Please select a different month.' });
            setIsSubmitting(false);
            return;
        }

        if (isDraft) {
            setTitle("Save Invoice?");
            setConfirmationTest("Are you sure you want to save invoice as draft?");
            setConfirmationTitleButton("Save Invoice");
        } else {
            setTitle("Create Invoice?");
            setConfirmationTest("Are you sure you want to create?");
            setConfirmationTitleButton("Create Invoice");
        }

        onOpen();
        setDataAdd(data);
        setIsSubmitting(true);
    }

    const generateDataAdd = async () => {

        const data = new FormData();
        data.append('InvoiceNumber', dataAdd.invoiceID || '');
        data.append('Type', 'reviewer');
        data.append('InvoiceDate', dataAdd.invoiceDate ? format(dataAdd.invoiceDate, FORMAT_ADD_DATE) : '');
        data.append('InspectionCompanyId', companyId);
        data.append('InspectionCompanyName', companyName);
        data.append('ReviewMonth', String(selectedMonth));
        data.append('ReviewYear', String(selectedYear));
        data.append('TotalReview', String(totalReviews));
        data.append('CostReview', String(reviewCost));
        data.append('TotalAmount', String(totalCost));
        data.append('Currency', currency);
        data.append('Notes', '');
        data.append('InvoiceStatus', String(isDraft ? INVOICE_STATUS.DRAFT : INVOICE_STATUS.IN_PROGRESS));
        data.append('PaymentStatus', String(PAYMENT_STATUS.UNPAID));
        //data.append('InvoiceDetails', JSON.stringify(listSireReview));

        const listResponse = await getListSireReviewsSimpleForReviewer(selectedMonth, selectedYear, companyId, 1, totalReviews);
        if (listResponse.data) {
            const listSireReviewsAll = listResponse.data as SireReviewSimpleRequestor[];
            listSireReviewsAll.map((review, index) => {
                /*const {
                    created,
                    createdBy,
                    modified,
                    modifiedBy,
                    isDeleted,
                    ...cleaned
                } = review*/
                //data.append('InvoiceDetails', JSON.stringify(cleaned));
                data.append(`InvoiceDetails[${index}].id`, review.id);
                data.append(`InvoiceDetails[${index}].sequenceId`, review.sequenceId);
                data.append(`InvoiceDetails[${index}].requestorId`, review.requestorId);
                data.append(`InvoiceDetails[${index}].requestor`, review.requestorName);
                data.append(`InvoiceDetails[${index}].vesselId`, review.vesselId);
                data.append(`InvoiceDetails[${index}].vesselName`, review.vesselName);
                data.append(`InvoiceDetails[${index}].vesselType`, review.vesselType);
                data.append(`InvoiceDetails[${index}].imoNumber`, review.imoNumber);
                data.append(`InvoiceDetails[${index}].addAldPertamina`, review.addAldPertamina);
                data.append(`InvoiceDetails[${index}].reviewDate`, review.reviewDate);
            });
        }
        
        uploadedFiles.map((file) => {
            data.append(`files`, file);
        });
        return data;

        /*let invoiceDetails = []

        const listResponse = await getListSireReviewsSimpleForReviewer(selectedMonth, selectedYear, companyId, 1, totalReviews);
        if (listResponse.data) {
            invoiceDetails = listResponse.data
        }

        return {
            invoiceNumber: dataAdd.invoiceID || '',
            type: 'reviewer',
            invoiceDate: dataAdd.invoiceDate ? format(dataAdd.invoiceDate, FORMAT_ADD_DATE) : '',
            inspectionCompanyId: companyId,
            inspectionCompanyName: companyName,
            reviewMonth: selectedMonth,
            reviewYear: selectedYear,
            totalReview: totalReviews,
            costReview: reviewCost,
            totalAmount: totalCost,
            currency: dataAdd.currency,
            notes: '',
            invoiceStatus: isDraft ? INVOICE_STATUS.DRAFT : INVOICE_STATUS.FINANCE,
            paymentStatus: PAYMENT_STATUS.UNPAID,
            invoiceDetails: invoiceDetails,
        }*/
    };

    /*const generateFilesData = (id: string) => {
        const data = new FormData();
        //data.append('id', id);
        uploadedFiles.map((file) => {
            data.append('files', file);
        });
        return data;
    };*/

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel Creating Invoice?") {
            navigate('/manage-invoices/sire-inspector');
            return;
        }

        const obj = await generateDataAdd();
        let response
        if (id){
            obj.append('id', id);
            response = await postUpdateSireReviewReviewerInvoice(obj);
        } else {
            response = await postCreateInvoicesSireReviewReviewer(obj);
        }
        
        setIsSubmitting(false);
        if (response?.status === HttpStatusCode.Ok) {
            //const responseFiles = await postCreateInvoicesSireReviewReviewerFiles(generateFilesData(response.data))
            //if (responseFiles?.status === HttpStatusCode.Ok){
                    toast({
                    title: 'Invoice created.',
                    description: "We've created the invoice for you.",
                    status: 'success',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            /*} else {
                toast({
                    title: 'Inspector failed to create.',
                    description: "File upload failed.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            }*/

            setTimeout(() => {
                navigate('/manage-invoices/sire-inspector')
            }, 1000)
        } else {
            toast({
                title: 'Inspector failed to create.',
                description: "We could not create the invoice for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleChangeMonth = (month: number, year: number, props: FormikProps<FormContents>) => {
        props.setFieldValue('reviewMonth', month);
        props.setFieldValue('reviewYear', year);
        setSelectedMonth(month);
        setSelectedYear(year);
    };

    const showToast = (error: any) => {
        toast({
            title: error?.code,
            description: error?.message,
            status: 'error',
            duration: DURATION_TOAST,
            isClosable: true,
        });
    };

    const getListSireReviews = async () => {
        setIsLoading(true);

        try {
            let response;
            if (id){
                response = await getListSireReviewsSimpleByInvoice(id, currentPageNumber, currentPageSize);
            } else {
                response = await getListSireReviewsSimpleForReviewer(selectedMonth, selectedYear, companyId, currentPageNumber, currentPageSize);
            }
            //console.log(response)
            
            if (response?.status === HttpStatusCode.Ok) {
                if (response.totalItem === 0){
                    //showToast({ code: 'No suitable SIRE reviews', message: 'Please select a different month.' });
                    setListSireReview([]);
                    setTotalReviews(0)
                    setTotalPageNumber(0);
                    setHasNextPage(false);
                    return
                }
                const originalData = response.data as SireReviewSimpleRequestor[];
                
                const transformedData: SireDataSimple[] = originalData.map((data) => ({
                    ...data,
                    addaldPertamina: data.addAldPertamina,
                    requestor: data.requestorName,
                }))
                setListSireReview(transformedData);
                setTotalReviews(response?.totalItem)
                setTotalPageNumber(response?.totalPage);
                setHasNextPage(response?.hasNextPage);
            } else {
                showToast(response?.error);
                setListSireReview([]);
                setTotalReviews(0)
                setTotalPageNumber(0);
                setHasNextPage(false);
            }
        } catch (error) {
            showToast({ code: 'Error', message: 'Failed to fetch data' });
            setListSireReview([]);
            setTotalReviews(0)
            setTotalPageNumber(0);
            setHasNextPage(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDrop = (acceptedFiles: File[], fieldName: string, setFieldValue: (fieldName: string, value: any) => void) => {
        setUploadedFiles(acceptedFiles);
        setFieldValue(fieldName, acceptedFiles);
    };

    const handleChangeCurrency = (props: FormikProps<FormContents>, prefix: string, code: string) => {
        props.setFieldValue('currency', code);
        setPrefix(prefix);
        setCurrency(code);
    };

    const handleChangeReviewCost = (value: number, setFieldValue: (fieldName: string, value: any) => void) => {
        setFieldValue(`reviewCost`, value);
        setReviewCost(value);
    };

    const getInvoiceDetails = async (id: string) => {
        setIsSubmitting(true);
        const res = await getSireReviewReviewerInvoiceDetails(id);
        if (res?.data){
            const data = res.data;
            setInitialContents({
                invoiceID: data.invoiceNumber,
                invoiceDate: data.invoiceDate,
                currency: data.currency,
                reviewCost: data.costReview,
                invoiceFile: [],
            });
					setPrefix(data.currency === CURRENCY.USD.code ? CURRENCY.USD.code : CURRENCY.IDR.code);
            setCurrency(data.currency);
            setSelectedMonth(data.reviewMonth);
            setSelectedYear(data.reviewYear);
            setReviewCost(data.costReview);
            /*const originalData = data.invoiceDetails as SireReviewSimple[];
            const transformedData: SireDataSimple[] = originalData.map((data) => ({
                ...data,
                addaldPertamina: data.addAldPertamina,
            }))
            setListSireReview(transformedData)*/
        }
        setIsSubmitting(false);
    }

    useEffect(() => {
        if (id){
            getInvoiceDetails(id)
        }
    }, []);

    /*useEffect(() => {
        getListSireReviews();
    }, []);*/

    useEffect(() => {
        if (!id){
            getListSireReviews();
        }
    }, [selectedMonth, selectedYear])

    useEffect(() => {
        getListSireReviews();
    }, [currentPageNumber, currentPageSize])

    useEffect(() => {
        setTotalCost(reviewCost * totalReviews);
    }, [reviewCost, totalReviews])

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
            <div className="form-container">
                <Formik
                    initialValues={initialContents}
                    validationSchema={() => invoiceSireReviewReviewerSchema()}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                    enableReinitialize
                >
                    {(props: FormikProps<FormContents>) => (
                        <Form>
                            <Field name='invoiceID'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.invoiceID && form.touched.invoiceID)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Invoice Number<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Invoice Number' className="form-input" isDisabled={isSubmitting} />
                                                <FormErrorMessage>{String(form.errors.invoiceID)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='invoiceDate'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.invoiceDate && form.touched.invoiceDate)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Invoice Date<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div style={{
                                                width: '100%',
                                                height: '2.5rem',
                                                fontSize: '1rem',
                                                paddingInlineStart: '1rem',
                                                paddingInlineEnd: '1rem',
                                                borderRadius: '0.375rem',
                                                minWidth: '0px',
                                                outline: '2px solid transparent',
                                                outlineOffset: '2px',
                                                position: 'relative',
                                                appearance: 'none',
                                                transitionProperty: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
                                                paddingTop: '0.35rem',
                                                marginBottom: (form.errors.invoiceDate && form.touched.invoiceDate) ? '1.25rem' : 0,
                                                border: form.errors.invoiceDate && form.touched.invoiceDate ? '2px solid red' : '1px solid #e5e7e9'
                                            }}>
                                                <DatePicker
                                                    id="invoiceDate"
                                                    selected={field.value as Date | null}
                                                    onChange={(date: Date | null) => props.setFieldValue('invoiceDate', date)}
                                                    dateFormat="dd-MM-YYYY"
                                                    placeholderText="Invoice Date"
                                                    readOnly={isSubmitting}
                                                />
                                                <FormErrorMessage>{String(form.errors.invoiceDate)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            {/*<Field name='singleMulti'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Single/Monthly</FormLabel>
                                            <RadioGroup className="radio-group" value={isMultiple ? "multi" : "single"} onChange={(e) => {setIsMultiple(e === "multi")}}>
                                                <Radio value="single"><span className="radio-option">Single</span></Radio>
                                                <Radio value="multi"><span className="radio-option">Monthly</span></Radio>
                                            </RadioGroup>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>*/}
                            <Field name='month'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.month && form.touched.month)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Review Month<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <MonthSelector
                                                    onChange={(month, year) => handleChangeMonth(month, year, props)}
                                                    month={selectedMonth} year={selectedYear} allowAllMonths={false}
                                                    disabled={isSubmitting || id}
                                                />
                                                <FormErrorMessage>{String(form.errors.month)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='currency'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.currency && form.touched.currency)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel htmlFor='status-switch' className="form-label">
                                                Currency<span style={{ color: 'red' }}>*</span>
                                            </FormLabel>
                                            <div className="form-input-container">
                                                <SelectCurrency
                                                    name='currency'
                                                    placeholder="Choose Currency"
                                                    handleChange={({ value, label }: any) => handleChangeCurrency(props, value, label)}
                                                    value={prefix}
                                                    //defaultValue={props.values.currency}
                                                    hasError={form.errors.currency && form.touched.currency}
                                                    isDisabled={isSubmitting}
                                                />
                                                <FormErrorMessage>{String(form.errors.currency)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='reviewCost'>
                                {({ field, form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.reviewCost && form.touched.reviewCost)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Cost per Review<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <CurrencyInput
                                                    {...field}
                                                    placeholder='Cost per Review'
                                                    prefix={prefix}
                                                    value={props.values.reviewCost}
                                                    error={Boolean(form.errors.reviewCost && form.touched.reviewCost)}
                                                    onValueChange={({ floatValue = 0 }) => handleChangeReviewCost(floatValue, props.setFieldValue)}
                                                    disabled={isSubmitting}
                                                />
                                                <FormErrorMessage>{String(form.errors.reviewCost)}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='totalReviews'>
                                {({ field }: FieldProps) => (
                                    <FormControl style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Total Reviews</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} value={totalReviews} type="number" placeholder='Total Reviews' className="form-input" isDisabled />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='totalCost'>
                                {({ field }: FieldProps) => (
                                    <FormControl style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Total Cost</FormLabel>
                                            <div className="form-input-container">
                                                <CurrencyInput
                                                    {...field}
                                                    placeholder='Total Cost'
                                                    prefix={prefix}
                                                    value={totalCost}
                                                    disabled
                                                />
                                            </div>
                                            
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='files'>
                                {({ form }: FieldProps) => (
                                    <FormControl isInvalid={Boolean(form.errors.files && form.submitCount)} style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Invoice File<span style={{ color: 'red' }}>*</span></FormLabel>
                                            <div className="form-input-container">
                                                <EditFile
                                                    isDisabled={isSubmitting}
                                                    isEditable
                                                    isInvalid={Boolean(form.errors.files && form.submitCount)}
                                                    handleDrop={(e) => handleDrop(e, 'files', props.setFieldValue)}
                                                    title="Invoice Files"
                                                    type="supporting-files"
                                                />
                                                <FormErrorMessage>{form.submitCount ? String(form.errors.files) : ''}</FormErrorMessage>
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>

                            <Suspense fallback={<Loader />}>
                                <TableSireReviewSimple
                                    dataList={listSireReview}
                                    isLoading={isLoading}
                                    currentPageSize={currentPageSize}
                                    handleChangePageSize={setCurrentPageSize}
                                    handleChangePageNumber={setCurrentPageNumber}
                                    currentPageNumber={currentPageNumber}
                                    totalPageNumber={totalPageNumber}
                                    hasNextPage={hasNextPage}
                                    role={role}
                                />
                                {listSireReview.length === 0 && !isLoading && <span>No suitable SIRE reviews. Please select a different month.</span>}
                            </Suspense>

                            <Divider />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 10 }}>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={() => {
                                        setIsDraft(true);
                                    }}
                                    type='submit'
                                >
                                    Save as Draft
                                </Button>
                                <Button
                                    mt={4}
                                    bgColor={COLORS.BUTTON.BACKGROUND}
                                    color={COLORS.BUTTON.FOREGROUND}
                                    isLoading={isSubmitting}
                                    onClick={() => {
                                        setIsDraft(false); 
                                    }}
                                    type='submit'
                                    rightIcon={<ArrowIcon />}
                                >
                                    Send Invoice
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    )
}

export default AddInvoicesSireInspector;
